import React, { useState, useEffect, useMemo } from 'react';
import { Wallet, History, X, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

const fmtMoney = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN');
const todayStr = () => new Date().toISOString().split('T')[0];
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

const SHORTAGE_FILTER_OPS = {
  '>=': (a, b) => a >= b,
  '<=': (a, b) => a <= b,
  '>': (a, b) => a > b,
  '<': (a, b) => a < b,
  '=': (a, b) => a === b,
  '!=': (a, b) => a !== b,
};

const Salary = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const [daysPresentMap, setDaysPresentMap] = useState({});
  const [shortageSelections, setShortageSelections] = useState({});

  const [activeShortageId, setActiveShortageId] = useState(null);
  const [shortageFilterOp, setShortageFilterOp] = useState('>=');
  const [shortageFilterValue, setShortageFilterValue] = useState('');

  const [activeAdvanceId, setActiveAdvanceId] = useState(null);
  const [advanceForm, setAdvanceForm] = useState({ business_date: todayStr(), amount: '', note: '' });

  const [activeHistoryId, setActiveHistoryId] = useState(null);
  const [historyLedger, setHistoryLedger] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ business_date: todayStr(), amount: '', note: '' });

  const fetchRows = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/salary/payroll-preview-batch?month=${month}&year=${year}`, { headers: authHeaders() });
      const json = await res.json();
      if (Array.isArray(json)) {
        setRows(json);
        setDaysPresentMap({});
        setShortageSelections(Object.fromEntries(json.map(r => [r.employee.id, new Set(r.pendingShortages.map(s => s.id))])));
      } else {
        toast.error(json.error || 'Failed to load payroll data');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const rowById = (id) => rows.find(r => r.employee.id === id);

  // After an action on one employee, refresh just that row so nobody else's
  // in-progress days-present entries on the sheet get wiped out.
  const refreshRow = async (employeeId) => {
    try {
      const res = await fetch(`/api/salary/payroll-preview/${employeeId}?month=${month}&year=${year}`, { headers: authHeaders() });
      const json = await res.json();
      if (!json.error) {
        setRows(prev => prev.map(r => r.employee.id === employeeId ? json : r));
        setShortageSelections(prev => ({ ...prev, [employeeId]: new Set(json.pendingShortages.map(s => s.id)) }));
        setDaysPresentMap(prev => ({ ...prev, [employeeId]: '' }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const grossFor = (row) => (parseFloat(daysPresentMap[row.employee.id]) || 0) * row.perDayRate;
  const shortageTotalFor = (row) => {
    const sel = shortageSelections[row.employee.id] || new Set();
    return row.pendingShortages.filter(s => sel.has(s.id)).reduce((sum, s) => sum + parseFloat(s.shortage), 0);
  };
  const netFor = (row) => grossFor(row) - shortageTotalFor(row);

  const toggleShortage = (employeeId, shortageId) => {
    setShortageSelections(prev => {
      const set = new Set(prev[employeeId] || []);
      if (set.has(shortageId)) set.delete(shortageId); else set.add(shortageId);
      return { ...prev, [employeeId]: set };
    });
  };

  const activeShortageRow = activeShortageId ? rowById(activeShortageId) : null;
  const filteredShortages = useMemo(() => {
    if (!activeShortageRow) return [];
    const threshold = parseFloat(shortageFilterValue);
    if (shortageFilterValue === '' || isNaN(threshold)) return activeShortageRow.pendingShortages;
    const cmp = SHORTAGE_FILTER_OPS[shortageFilterOp];
    return activeShortageRow.pendingShortages.filter(s => cmp(parseFloat(s.shortage), threshold));
  }, [activeShortageRow, shortageFilterOp, shortageFilterValue]);

  const selectFiltered = () => {
    setShortageSelections(prev => {
      const set = new Set(prev[activeShortageId] || []);
      filteredShortages.forEach(s => set.add(s.id));
      return { ...prev, [activeShortageId]: set };
    });
  };
  const deselectFiltered = () => {
    setShortageSelections(prev => {
      const set = new Set(prev[activeShortageId] || []);
      filteredShortages.forEach(s => set.delete(s.id));
      return { ...prev, [activeShortageId]: set };
    });
  };

  const openShortageModal = (row) => {
    setShortageFilterOp('>=');
    setShortageFilterValue('');
    setActiveShortageId(row.employee.id);
  };

  const handleFinalize = async (row) => {
    const days = parseFloat(daysPresentMap[row.employee.id]);
    if (!days || days <= 0) return toast.error('Enter days present first.');
    const gross = grossFor(row);
    const net = netFor(row);
    if (!window.confirm(`Record salary for ${row.employee.name}?\n\nDays present: ${days}\nGross salary: ₹${fmtMoney(gross)}\nNet payable: ₹${fmtMoney(net)}`)) return;

    setSavingId(row.employee.id);
    try {
      const res = await fetch('/api/salary/finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          employee_id: row.employee.id,
          month, year,
          days_present: days,
          gross_salary: gross,
          shortage_ids: Array.from(shortageSelections[row.employee.id] || [])
        })
      });
      const json = await res.json();
      if (json.status === 'success') {
        toast.success(`Salary recorded for ${row.employee.name}`);
        await refreshRow(row.employee.id);
      } else {
        toast.error(json.error || 'Failed to record salary');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setSavingId(null);
  };

  const openAdvanceModal = (row) => {
    setAdvanceForm({ business_date: todayStr(), amount: '', note: '' });
    setActiveAdvanceId(row.employee.id);
  };

  const submitAdvance = async (e) => {
    e.preventDefault();
    if (!advanceForm.amount) return;
    try {
      const res = await fetch('/api/salary/advance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ employee_id: activeAdvanceId, ...advanceForm })
      });
      const json = await res.json();
      if (json.status === 'success') {
        toast.success('Advance recorded');
        await refreshRow(activeAdvanceId);
        setActiveAdvanceId(null);
      } else toast.error(json.error);
    } catch (err) { toast.error(err.message); }
  };

  const openHistoryModal = async (row) => {
    setActiveHistoryId(row.employee.id);
    setHistoryLedger(null);
    setPaymentForm({ business_date: todayStr(), amount: '', note: '' });
    try {
      const res = await fetch(`/api/salary/ledger/${row.employee.id}`, { headers: authHeaders() });
      setHistoryLedger(await res.json());
    } catch (err) {
      toast.error(err.message);
    }
  };

  const reloadHistoryLedger = async () => {
    const res = await fetch(`/api/salary/ledger/${activeHistoryId}`, { headers: authHeaders() });
    setHistoryLedger(await res.json());
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount) return;
    try {
      const res = await fetch('/api/salary/payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ employee_id: activeHistoryId, ...paymentForm })
      });
      const json = await res.json();
      if (json.status === 'success') {
        toast.success('Payment recorded');
        setPaymentForm({ business_date: todayStr(), amount: '', note: '' });
        await reloadHistoryLedger();
        await refreshRow(activeHistoryId);
      } else toast.error(json.error);
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteTxn = async (id) => {
    if (!window.confirm('Remove this entry? This will change the running balance.')) return;
    try {
      await fetch(`/api/salary/${id}`, { method: 'DELETE', headers: authHeaders() });
      await reloadHistoryLedger();
      await refreshRow(activeHistoryId);
    } catch (err) { toast.error(err.message); }
  };

  const activeHistoryRow = activeHistoryId ? rowById(activeHistoryId) : null;

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-16">
      <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl p-8 text-white shadow-lg">
        <h1 className="text-3xl font-black mb-2 flex items-center gap-3">
          <Wallet size={32} /> Salary
        </h1>
        <p className="text-blue-100 font-medium text-lg">Fill in days present, review shortages/advances, and pay - one row per employee.</p>
      </div>

      <div className="glass-panel p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Month</label>
          <select className="input-field w-auto py-2" value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Year</label>
          <input type="number" className="input-field w-28 py-2" value={year} onChange={e => setYear(Number(e.target.value))} />
        </div>
      </div>

      <div className="glass-panel overflow-hidden">
        {isLoading ? (
          <div className="text-center text-slate-500 py-10">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-slate-500 py-10">No active employees found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left whitespace-nowrap">
              <thead className="bg-slate-50 text-slate-500 text-xs font-semibold border-b border-slate-100">
                <tr>
                  <th className="p-3">Employee</th>
                  <th className="p-3 text-right">Monthly Salary</th>
                  <th className="p-3 text-right">Per-Day Rate</th>
                  <th className="p-3">Days Present</th>
                  <th className="p-3 text-right">Gross Salary</th>
                  <th className="p-3 text-center">Shortage</th>
                  <th className="p-3 text-center">Advance</th>
                  <th className="p-3 text-right">Net Payable</th>
                  <th className="p-3 text-right">Balance</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(row => {
                  const sel = shortageSelections[row.employee.id] || new Set();
                  const selectedCount = row.pendingShortages.filter(s => sel.has(s.id)).length;
                  const shortageTotal = shortageTotalFor(row);
                  const gross = grossFor(row);
                  const net = netFor(row);
                  return (
                    <tr key={row.employee.id} className="hover:bg-slate-50/50">
                      <td className="p-3 font-medium text-slate-800">
                        {row.employee.name}
                        {row.alreadyRunThisMonth && (
                          <span className="ml-2 text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">RUN</span>
                        )}
                      </td>
                      <td className="p-3 text-right text-slate-600">₹{fmtMoney(row.employee.monthly_salary)}</td>
                      <td className="p-3 text-right text-slate-600">₹{fmtMoney(row.perDayRate)}</td>
                      <td className="p-3">
                        <input
                          type="number" step="0.5" placeholder="0"
                          className="input-field w-20 py-1.5 px-2 text-sm"
                          value={daysPresentMap[row.employee.id] || ''}
                          onChange={e => setDaysPresentMap(prev => ({ ...prev, [row.employee.id]: e.target.value }))}
                        />
                      </td>
                      <td className="p-3 text-right font-semibold text-slate-800">₹{fmtMoney(gross)}</td>
                      <td className="p-3 text-center">
                        <button onClick={() => openShortageModal(row)} className={`text-xs font-semibold px-2 py-1 rounded-lg transition-colors ${shortageTotal > 0 ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}>
                          {row.pendingShortages.length === 0 ? 'None' : `-₹${fmtMoney(shortageTotal)} (${selectedCount}/${row.pendingShortages.length})`}
                        </button>
                      </td>
                      <td className="p-3 text-center">
                        <button onClick={() => openAdvanceModal(row)} className="text-xs font-semibold px-2 py-1 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                          {row.advanceThisMonth > 0 ? `₹${fmtMoney(row.advanceThisMonth)}` : '+ Give'}
                        </button>
                      </td>
                      <td className={`p-3 text-right font-bold ${net < 0 ? 'text-red-600' : 'text-slate-900'}`}>₹{fmtMoney(net)}</td>
                      <td className={`p-3 text-right font-mono ${row.currentBalance < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                        {row.currentBalance < 0 ? '-' : '+'}₹{fmtMoney(Math.abs(row.currentBalance))}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleFinalize(row)}
                            disabled={savingId === row.employee.id}
                            className="text-xs font-bold px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white transition-colors disabled:opacity-50"
                          >
                            {savingId === row.employee.id ? '...' : 'Pay'}
                          </button>
                          <button onClick={() => openHistoryModal(row)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                            <History className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {activeShortageRow && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800">Shortages - {activeShortageRow.employee.name}</h3>
              <button onClick={() => setActiveShortageId(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {activeShortageRow.pendingShortages.length > 0 ? (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Uncheck any you don't want deducted this time:</p>

                  <div className="flex items-center gap-2 mb-2 flex-wrap text-sm bg-slate-50 border border-slate-100 rounded-xl p-2">
                    <span className="text-slate-500 text-xs font-medium">Filter amount</span>
                    <select className="input-field w-auto py-1 px-2 text-sm" value={shortageFilterOp} onChange={e => setShortageFilterOp(e.target.value)}>
                      <option value=">=">&gt;=</option>
                      <option value="<=">&lt;=</option>
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                      <option value="=">=</option>
                      <option value="!=">&ne;</option>
                    </select>
                    <input type="number" className="input-field w-24 py-1 px-2 text-sm" placeholder="e.g. 100" value={shortageFilterValue} onChange={e => setShortageFilterValue(e.target.value)} />
                    <button type="button" onClick={selectFiltered} className="text-xs font-semibold text-emerald-600 hover:text-emerald-700">Select</button>
                    <button type="button" onClick={deselectFiltered} className="text-xs font-semibold text-red-500 hover:text-red-600">Deselect</button>
                    {shortageFilterValue !== '' && (
                      <span className="text-xs text-slate-400 ml-auto">{filteredShortages.length} of {activeShortageRow.pendingShortages.length} match</span>
                    )}
                  </div>

                  {filteredShortages.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto p-1">
                      {filteredShortages.map(s => {
                        const checked = (shortageSelections[activeShortageRow.employee.id] || new Set()).has(s.id);
                        return (
                          <label
                            key={s.id}
                            className={`flex items-center justify-between gap-2 p-3 rounded-xl border cursor-pointer shadow-sm transition-colors text-sm ${
                              checked ? 'bg-white border-slate-200 hover:border-slate-300' : 'bg-slate-50 border-slate-100 opacity-50'
                            }`}
                          >
                            <span className="flex items-center gap-2 text-slate-700 font-medium">
                              <input type="checkbox" className="shrink-0" checked={checked} onChange={() => toggleShortage(activeShortageRow.employee.id, s.id)} />
                              <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                              <span>{fmtDate(s.business_date)}</span>
                            </span>
                            <span className="font-bold text-red-600 shrink-0">-₹{fmtMoney(s.shortage)}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-3">No shortages match this filter.</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No pending shortages for this employee.</p>
              )}

              <div className="border-t border-slate-100 pt-3 flex justify-between text-base font-bold">
                <span>Selected total</span>
                <span className="text-red-600">-₹{fmtMoney(shortageTotalFor(activeShortageRow))}</span>
              </div>

              <button onClick={() => setActiveShortageId(null)} className="w-full btn-primary py-2.5">Done</button>
            </div>
          </div>
        </div>
      )}

      {activeAdvanceId && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800">Give Advance - {rowById(activeAdvanceId)?.employee.name}</h3>
              <button onClick={() => setActiveAdvanceId(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submitAdvance} className="p-6 space-y-3">
              <input type="date" className="input-field" value={advanceForm.business_date} onChange={e => setAdvanceForm({ ...advanceForm, business_date: e.target.value })} />
              <input type="number" className="input-field" placeholder="Amount (₹)" value={advanceForm.amount} onChange={e => setAdvanceForm({ ...advanceForm, amount: e.target.value })} autoFocus />
              <input type="text" className="input-field" placeholder="Note (optional)" value={advanceForm.note} onChange={e => setAdvanceForm({ ...advanceForm, note: e.target.value })} />
              <button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-2.5 rounded-xl transition-colors">Give Advance</button>
            </form>
          </div>
        </div>
      )}

      {activeHistoryId && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-slate-100 shrink-0">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-500" /> {activeHistoryRow?.employee.name} - Account History
              </h3>
              <button onClick={() => setActiveHistoryId(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-4">
              <form onSubmit={submitPayment} className="flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-100 rounded-xl p-3">
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 mb-1">Date</label>
                  <input type="date" className="input-field py-1.5 px-2 text-sm w-36" value={paymentForm.business_date} onChange={e => setPaymentForm({ ...paymentForm, business_date: e.target.value })} />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 mb-1">Amount</label>
                  <input type="number" className="input-field py-1.5 px-2 text-sm w-28" placeholder="₹" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
                </div>
                <div className="flex-1 min-w-[100px]">
                  <label className="block text-[10px] font-medium text-slate-500 mb-1">Note</label>
                  <input type="text" className="input-field py-1.5 px-2 text-sm" placeholder="optional" value={paymentForm.note} onChange={e => setPaymentForm({ ...paymentForm, note: e.target.value })} />
                </div>
                <button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm py-2 px-4 rounded-xl transition-colors">Record Payment</button>
              </form>

              {!historyLedger ? (
                <div className="text-center text-slate-500 py-8">Loading...</div>
              ) : historyLedger.transactions.length === 0 ? (
                <div className="text-center text-slate-500 py-8">No transactions yet.</div>
              ) : (
                <div className="overflow-x-auto border border-slate-100 rounded-xl">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 text-slate-500 text-xs font-semibold border-b border-slate-100">
                      <tr>
                        <th className="p-3">Date</th>
                        <th className="p-3">Type</th>
                        <th className="p-3">Note</th>
                        <th className="p-3 text-right">Amount</th>
                        <th className="p-3 text-right">Balance</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {historyLedger.transactions.map(t => (
                        <tr key={t.id} className="hover:bg-slate-50/50">
                          <td className="p-3 text-slate-600">{fmtDate(t.business_date)}</td>
                          <td className="p-3">
                            <span className={`text-xs font-bold px-2 py-1 rounded ${t.type === 'SALARY' ? 'bg-blue-100 text-blue-700' : t.type === 'ADVANCE' ? 'bg-amber-100 text-amber-700' : t.type === 'SHORTAGE' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {t.type}
                            </span>
                          </td>
                          <td className="p-3 text-slate-500 text-xs">{t.note || '-'}</td>
                          <td className="p-3 text-right font-mono">{t.type === 'SALARY' ? '+' : '-'}₹{fmtMoney(t.amount)}</td>
                          <td className={`p-3 text-right font-mono font-bold ${t.balance < 0 ? 'text-red-600' : 'text-emerald-700'}`}>₹{fmtMoney(t.balance)}</td>
                          <td className="p-3 text-right">
                            <button onClick={() => handleDeleteTxn(t.id)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Salary;
